/**
 * Pathfinder-shaped RSID: validate URL param, reuse `localStorage`, or generate a new id for the iframe query.
 */
import { getTrackingParamsFromUrl } from '../utils/tracking';

/** Matches PathfinderClient `isRSID` — 16 chars. */
const PATHFINDER_RSID_RE = /^[A-Z]{1}[L0-9]{1}[0-9]{2}[a-z0-9]{12}$/;

const RSID_STORAGE_KEY = 'eceq:rsid:v1';

/** True if `value` matches the 16-character Pathfinder RSID pattern. */
export function isPathfinderRsid(value: string): boolean {
  return PATHFINDER_RSID_RE.test(value.trim());
}

/** CSPRNG-free token segment for generated RSIDs (Pathfinder-compatible charset). */
function randomAlnumLower(length: number): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let s = '';
  for (let i = 0; i < length; i++) {
    s += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return s;
}

/**
 * Same shape as Pathfinder `session-store` generation: brand letter + three-digit
 * segment (100–999) merged into positions 2–4 + 12 lowercase alnum.
 */
export function generatePathfinderRsid(brandLetter: string): string {
  const c = (brandLetter || 'E').slice(0, 1).toUpperCase();
  const brand = /^[A-Z]$/.test(c) ? c : 'E';
  const numericSegment = Math.floor(100 + Math.random() * 900);
  const numStr = String(numericSegment);
  const alphanumericSegment = randomAlnumLower(12);
  return `${brand}${numStr[0]}${numStr[1]}${numStr[2]}${alphanumericSegment}`;
}

/** First character of `VITE_RSID_BRAND_LETTER`, validated A–Z, default `E`. */
export function getRsidBrandLetterFromEnv(): string {
  const raw = (import.meta.env.VITE_RSID_BRAND_LETTER ?? 'E').trim();
  const letter = raw.slice(0, 1).toUpperCase();
  return /^[A-Z]$/.test(letter) ? letter : 'E';
}

function persistRsid(rsid: string): void {
  try {
    localStorage.setItem(RSID_STORAGE_KEY, rsid);
  } catch {
    /* private mode / blocked */
  }
}

/**
 * Prefer valid `?rsid=` from the page; else reuse persisted RSID; else generate,
 * persist, and return. Aligns with Pathfinder so the iframe query is accepted.
 */
export function resolveRsidForSession(): string {
  const fromUrl = getTrackingParamsFromUrl().rsid.trim();
  if (fromUrl && isPathfinderRsid(fromUrl)) {
    persistRsid(fromUrl);
    return fromUrl;
  }

  try {
    const stored = localStorage.getItem(RSID_STORAGE_KEY);
    if (stored && isPathfinderRsid(stored)) {
      return stored;
    }
  } catch {
    /* ignore */
  }

  const fresh = generatePathfinderRsid(getRsidBrandLetterFromEnv());
  persistRsid(fresh);
  return fresh;
}
